import React, {FC, useEffect, useState} from 'react'
import CommerciumLoaderHost from 'src/utils/CommerciumLoaderHost'
import {KTIcon} from 'src/marketConnect/helpers'
import {getDashboardComingSoonAsync} from 'src/redux/actions/authActions'
import {useAppDispatch} from 'src/redux/storeHooks'
import {DashboardNewsItem} from '../types/dashboard.types'

const HELP_BASE_URL = 'https://commercium-help.constacloud.com/'

const COMING_SOON_FALLBACK_MESSAGE =
  'No upcoming updates right now. Product news will appear here when published—see the Knowledge Base below for setup guides in the meantime.'

const knowledgeBaseLinks = [
  {label: 'Setting up Shopify Connection', href: 'https://commercium-help.constacloud.com/article/how-to-connect-shopify-store-to-commercium'},
  {label: 'Setting up Temu', href: 'https://commercium-help.constacloud.com/article/how-to-connect-temu-to-commercium'},
  {label: 'Product Listing Configuration', href: 'https://commercium-help.constacloud.com/article/getting-started-with-product-listings'},
]

const NewsUpdatesSidebar: FC = () => {
  const dispatch = useAppDispatch()
  const [comingSoonItems, setComingSoonItems] = useState<DashboardNewsItem[]>([])
  const [comingSoonLoading, setComingSoonLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setComingSoonLoading(true)

    dispatch(getDashboardComingSoonAsync())
      .then((response) => {
        if (cancelled) return

        const payloadData = response.payload?.data ?? response.payload
        const items: DashboardNewsItem[] = Array.isArray(payloadData)
          ? payloadData
          : Array.isArray(payloadData?.results)
          ? payloadData.results
          : Array.isArray(payloadData?.items)
          ? payloadData.items
          : []

        setComingSoonItems(
          items.filter(
            (item) =>
              item.type?.trim().toUpperCase() === 'COMING_SOON' &&
              (!item.status || item.status.trim().toLowerCase() === 'published')
          )
        )
      })
      .catch(() => {
        if (!cancelled) setComingSoonItems([])
      })
      .finally(() => {
        if (!cancelled) setComingSoonLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch])

  const hasComingSoonItems = comingSoonItems.length > 0

  return (
    <aside className='dashboard-news-sidebar'>
      <div className='dashboard-coming-soon-card'>
        <h4>Coming Soon</h4>
        {comingSoonLoading ? (
          <CommerciumLoaderHost variant='section' layout='centered' compact />
        ) : hasComingSoonItems ? (
          <ul>
            {comingSoonItems.map((item) => (
              <li key={item.id}>{item.title}</li>
            ))}
          </ul>
        ) : (
          <p className='dashboard-coming-soon-empty mb-0'>{COMING_SOON_FALLBACK_MESSAGE}</p>
        )}
      </div>

      <div className='dashboard-panel-card dashboard-knowledge-card mb-0'>
        <div className='dashboard-knowledge-header'>
          <KTIcon iconName='book-open' className='fs-3 text-primary' />
          <h4>Knowledge Base</h4>
        </div>
        <div className='dashboard-panel-body'>
          <p className='dashboard-knowledge-intro'>
            Need help setting up a new channel or ERP? Explore our detailed guides.
          </p>
          <ul className='dashboard-knowledge-links'>
            {knowledgeBaseLinks.map((link) => (
              <li key={link.label}>
                <a href={link.href} target='_blank' rel='noopener noreferrer'>
                  {link.label}
                  <KTIcon iconName='arrow-up-right' className='fs-7' />
                </a>
              </li>
            ))}
          </ul>
          <a
            className='dashboard-knowledge-footer'
            href={HELP_BASE_URL}
            target='_blank'
            rel='noopener noreferrer'
          >
            View All Documentation
            <KTIcon iconName='arrow-up-right' className='fs-7' />
          </a>
        </div>
      </div>

      <div className='dashboard-service-status'>
        <span className='dashboard-service-status-dot' />
        All Services Operational
      </div>
    </aside>
  )
}

export default NewsUpdatesSidebar
